import { useEffect, useState } from "react";
import axios from "axios";
import { Save, Loader2, CheckCircle2, Type } from "lucide-react";
import { DEFAULT_CONTENT, mergeContent } from "../lib/content";
import { API_BASE } from "../lib/site";

const GROUPS = [
    {
        id: "whatsapp",
        label: "Contato / WhatsApp",
        fields: [
            { key: "whatsapp.number", label: "Número do WhatsApp (só dígitos: 55 + DDD + número)" },
            { key: "whatsapp.display", label: "Telefone exibido no site" },
        ],
    },
    {
        id: "hero",
        label: "Topo (Hero)",
        fields: [
            { key: "hero.badge", label: "Etiqueta amarela" },
            { key: "hero.title1", label: "Título — linha 1" },
            { key: "hero.title2", label: "Título — linha 2 (manuscrita)" },
            { key: "hero.subtitle", label: "Subtítulo", area: true },
            { key: "hero.cta1", label: "Botão principal" },
            { key: "hero.cta2", label: "Botão do WhatsApp" },
        ],
    },
    {
        id: "beneficios",
        label: "Benefícios",
        fields: [
            { key: "beneficios.title1", label: "Título — parte 1" },
            { key: "beneficios.title2", label: "Título — parte destacada (laranja)" },
            { key: "beneficios.subtitle", label: "Subtítulo", area: true },
            { key: "beneficios.b1.t", label: "Card 1 — título" },
            { key: "beneficios.b1.d", label: "Card 1 — texto", area: true },
            { key: "beneficios.b2.t", label: "Card 2 — título" },
            { key: "beneficios.b2.d", label: "Card 2 — texto", area: true },
            { key: "beneficios.b3.t", label: "Card 3 — título" },
            { key: "beneficios.b3.d", label: "Card 3 — texto", area: true },
            { key: "beneficios.b4.t", label: "Card 4 — título" },
            { key: "beneficios.b4.d", label: "Card 4 — texto", area: true },
            { key: "beneficios.note1", label: "Anotação manuscrita (card verde)" },
            { key: "beneficios.note2", label: "Anotação manuscrita (card laranja)" },
        ],
    },
    {
        id: "passos",
        label: "Como funciona",
        fields: [
            { key: "passos.title", label: "Título da seção" },
            { key: "passos.subtitle", label: "Frase manuscrita" },
            ...[0, 1, 2, 3].flatMap((i) => [
                { key: `passos.steps.${i}.t`, label: `Passo ${i + 1} — título` },
                { key: `passos.steps.${i}.d`, label: `Passo ${i + 1} — texto`, area: true },
            ]),
        ],
    },
    {
        id: "sobre",
        label: "Quem somos (Missão, Visão e Valores)",
        fields: [
            { key: "sobre.title", label: "Título da seção" },
            { key: "sobre.subtitle", label: "Subtítulo", area: true },
            { key: "sobre.missao", label: "Missão", area: true },
            { key: "sobre.visao", label: "Visão", area: true },
            { key: "sobre.valores", label: "Valores", area: true },
        ],
    },
    {
        id: "sindicos",
        label: "Para síndicos",
        fields: [
            { key: "sindicos.title1", label: "Título — parte 1" },
            { key: "sindicos.title2", label: "Título — parte manuscrita" },
            { key: "sindicos.subtitle", label: "Subtítulo", area: true },
            ...[0, 1, 2, 3].flatMap((i) => [
                { key: `sindicos.pitches.${i}.t`, label: `Vantagem ${i + 1} — título` },
                { key: `sindicos.pitches.${i}.d`, label: `Vantagem ${i + 1} — texto`, area: true },
            ]),
            { key: "sindicos.sim_title", label: "Título do simulador" },
            { key: "sindicos.sim_note", label: "Nota abaixo do botão do simulador" },
        ],
    },
    {
        id: "categorias",
        label: "Categorias",
        fields: [
            { key: "categorias.title", label: "Título da seção" },
            { key: "categorias.subtitle", label: "Subtítulo", area: true },
            { key: "categorias.items.alimentos", label: "Itens de Alimentos (separe por ;)", area: true },
            { key: "categorias.items.bebidas", label: "Itens de Bebidas (separe por ;)", area: true },
            { key: "categorias.items.higiene", label: "Itens de Higiene (separe por ;)", area: true },
            { key: "categorias.items.limpeza", label: "Itens de Limpeza (separe por ;)", area: true },
            { key: "categorias.items.conveniencia", label: "Itens de Conveniência (separe por ;)", area: true },
        ],
    },
    {
        id: "corporativo",
        label: "Empresas corporativas",
        fields: [
            { key: "corporativo.tag", label: "Etiqueta da seção" },
            { key: "corporativo.title1", label: "Título — parte 1" },
            { key: "corporativo.title2", label: "Título — parte manuscrita" },
            { key: "corporativo.subtitle", label: "Subtítulo", area: true },
            ...[0, 1, 2, 3].flatMap((i) => [
                { key: `corporativo.pitches.${i}.t`, label: `Vantagem ${i + 1} — título` },
                { key: `corporativo.pitches.${i}.d`, label: `Vantagem ${i + 1} — texto`, area: true },
            ]),
            { key: "corporativo.cta", label: "Botão do WhatsApp" },
            { key: "corporativo.cta_note", label: "Nota abaixo do botão" },
        ],
    },
    {
        id: "galeria",
        label: "Galeria",
        fields: [
            { key: "galeria.title", label: "Título da seção" },
            { key: "galeria.subtitle", label: "Subtítulo", area: true },
        ],
    },
    {
        id: "novidades",
        label: "Novidades",
        fields: [
            { key: "novidades.title", label: "Título da seção" },
            { key: "novidades.subtitle", label: "Subtítulo", area: true },
        ],
    },
    {
        id: "faq",
        label: "FAQ (Perguntas frequentes)",
        fields: [
            { key: "faq.title", label: "Título da seção" },
            ...[0, 1, 2, 3, 4].flatMap((i) => [
                { key: `faq.items.${i}.q`, label: `Pergunta ${i + 1}` },
                { key: `faq.items.${i}.a`, label: `Resposta ${i + 1}`, area: true },
            ]),
        ],
    },
    {
        id: "form",
        label: "Formulário de implantação",
        fields: [
            { key: "form.title1", label: "Título — parte 1" },
            { key: "form.title2", label: "Título — parte manuscrita" },
            { key: "form.subtitle", label: "Subtítulo", area: true },
            { key: "form.b1", label: "Lista — item 1" },
            { key: "form.b2", label: "Lista — item 2" },
            { key: "form.b3", label: "Lista — item 3" },
        ],
    },
    {
        id: "footer",
        label: "Rodapé",
        fields: [
            { key: "footer.cta_title", label: "Chamada final (caixa verde)" },
            { key: "footer.cta_slogan", label: "Slogan manuscrito" },
            { key: "footer.atendimento", label: "Texto de atendimento" },
            { key: "footer.instagram", label: "Instagram (ex.: @conbox24)" },
        ],
    },
];

const getPath = (obj, path) =>
    path.split(".").reduce((cur, p) => (cur == null ? cur : cur[p]), obj);

const setPath = (obj, path, value) => {
    const nd = JSON.parse(JSON.stringify(obj));
    const parts = path.split(".");
    let cur = nd;
    for (let i = 0; i < parts.length - 1; i++) {
        if (cur[parts[i]] == null) cur[parts[i]] = {};
        cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
    return nd;
};

const inputCls =
    "w-full rounded-xl border-2 border-[#1A231E]/12 bg-white px-4 py-3 text-base font-semibold text-[#1A231E] focus:outline-none focus:border-[#F57A3A] transition-colors";

export default function SiteTexts() {
    const [draft, setDraft] = useState(null);
    const [group, setGroup] = useState("hero");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    useEffect(() => {
        axios
            .get(`${API_BASE}/content`)
            .then(({ data }) => setDraft(mergeContent(data && typeof data === "object" ? data : {})))
            .catch(() => setDraft(mergeContent()));
    }, []);

    const save = async () => {
        setSaving(true);
        setMsg(null);
        try {
            await axios.put(`${API_BASE}/content`, draft, { withCredentials: true });
            window.__CONBOX_WA = draft.whatsapp.number;
            setMsg({ ok: true, text: "Textos salvos! Recarregue a página do site para conferir." });
        } catch (err) {
            const d = err.response?.data?.detail;
            setMsg({ ok: false, text: typeof d === "string" ? d : "Não foi possível salvar. Tente entrar novamente." });
        } finally {
            setSaving(false);
        }
    };

    if (!draft) {
        return (
            <div className="py-20 grid place-items-center text-[#0E6B3F]">
                <Loader2 className="w-8 h-8 animate-spin" />
            </div>
        );
    }

    const active = GROUPS.find((g) => g.id === group);

    return (
        <div data-testid="site-texts-editor">
            <div className="flex items-center gap-3">
                <Type className="w-6 h-6 text-[#0E6B3F]" />
                <h2 className="text-2xl font-extrabold">Textos do site</h2>
            </div>
            <p className="mt-2 text-sm text-[#1A231E]/60 font-semibold">
                Edite qualquer texto das seções e clique em salvar. Deixe em branco para voltar ao padrão não é possível — use copiar do padrão antes de trocar.
            </p>

            <div className="mt-6 grid lg:grid-cols-[240px_1fr] gap-6 items-start">
                <div className="flex lg:flex-col flex-wrap gap-2">
                    {GROUPS.map((g) => (
                        <button
                            key={g.id}
                            data-testid={`texts-group-${g.id}`}
                            onClick={() => setGroup(g.id)}
                            className={`text-left text-sm font-bold px-4 py-2.5 rounded-xl transition-colors ${
                                group === g.id
                                    ? "bg-[#0E6B3F] text-[#F7F4EC]"
                                    : "bg-white border-2 border-[#1A231E]/10 text-[#1A231E]/70 hover:border-[#1A231E]/30"
                            }`}
                        >
                            {g.label}
                        </button>
                    ))}
                </div>

                <div className="rounded-[1.75rem] bg-white border-2 border-[#1A231E]/12 sticker-soft p-6 sm:p-7">
                    <div className="grid sm:grid-cols-2 gap-4">
                        {active.fields.map((f) => {
                            const value = getPath(draft, f.key) ?? "";
                            return (
                                <div key={f.key} className={f.area ? "sm:col-span-2" : ""}>
                                    <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5">
                                        {f.label}
                                    </label>
                                    {f.area ? (
                                        <textarea
                                            rows={3}
                                            value={value}
                                            onChange={(e) => setDraft((d) => setPath(d, f.key, e.target.value))}
                                            className={`${inputCls} resize-none`}
                                            data-testid={`texts-${f.key.replace(/\./g, "-")}`}
                                        />
                                    ) : (
                                        <input
                                            value={value}
                                            onChange={(e) => setDraft((d) => setPath(d, f.key, e.target.value))}
                                            className={inputCls}
                                            data-testid={`texts-${f.key.replace(/\./g, "-")}`}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <button
                        onClick={save}
                        data-testid="texts-save-button"
                        disabled={saving}
                        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0E6B3F] hover:bg-[#0A5531] text-[#F7F4EC] font-extrabold py-4 transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60"
                    >
                        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                        Salvar textos
                    </button>

                    {msg && (
                        <p
                            data-testid="texts-save-message"
                            className={`mt-4 rounded-xl px-4 py-3 text-sm font-semibold border-2 ${
                                msg.ok ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-600"
                            }`}
                        >
                            {msg.ok ? <CheckCircle2 className="w-4 h-4 inline mr-1.5 -mt-0.5" /> : null}
                            {msg.text}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
